/**
 * career-search — the flagship search surface (route `/search`).
 *
 * Shows the EFFECTIVE search config (returned by `/api/search/start`), a start
 * control, the LIVE push rail (the SearchRun store folded from the push plane,
 * `role="log" aria-live="polite"`, counts-with-meaning), and a real Stop control.
 * Opportunity cards populate as the detached run writes them: while a run is live
 * the surface polls `/api/opportunities` and reconciles them into the opportunity
 * store (each card upgrades verify → brief → match in place). Bookmark / dismiss /
 * delete are OPTIMISTIC store commands carrying the ABSOLUTE end-state; the next
 * reconcile folds server truth. All chrome flows through `t()`; card CONTENT
 * (authored brief, posting text, match quotes) is domain data, rendered as authored.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type PushChannel,
  type ReadonlySignal,
  type Router,
  type Signal,
  type Urn,
  type ViewModel,
} from "@platform/web-kit"
import { classMap, html, nothing, repeat, type TemplateResult } from "@platform/web-kit/render"
import {
  I18nToken,
  OpportunityStoreToken,
  PushToken,
  RouterToken,
  SearchRunStoreToken,
  SessionToken,
  apiGet,
  apiPost,
  isPaymentRequired,
  isSearchInputUnchanged,
} from "../services"
import { createStreamClaim, type StreamClaim } from "../lib/push-streams"
import type { CareerI18n, CareerKey } from "../../i18n"
import {
  careerStream,
  type OpportunityCardView,
  type OpportunityStore,
  type SearchRunStage,
  type SearchRunStatus,
  type SearchRunStore,
  type SearchRunView,
  type ShellSession,
} from "../domain"
import { action } from "../lib/action"
import { degradedNote } from "../lib/degraded"
import { writeLastGeneration } from "../lib/last-generation"
import { opportunityShortId, renderOpportunityGroup } from "../lib/opportunity-card"
import { groupByCompany, urgencyOf } from "../lib/opportunity-pipeline"
import { skeletonOpportunityRows } from "../lib/skeleton"
import { hubHero, HUB_TITLE_ID } from "../lib/hub"
import { nextStep } from "../lib/next-step"
import { spineDecl, toNextStep } from "../golden-path"

/** The effective search config the run uses (client projection of SearchConfig). */
interface SearchConfig {
  roles: string[]
  queries: string[]
  seniority: string[]
  locations: string[]
  excludeKeywords: string[]
  summary: string
}
interface StartResponse {
  searchId: string
  streamId: string
  config: SearchConfig
  targetingReady: boolean
}
interface OpportunityWithMatch {
  opportunity: Record<string, unknown>
  match: Record<string, unknown> | null
  /** The enriched employer digest for this result's org (E3b), when the read model carries it. */
  org?: Record<string, unknown>
  /** The GLOBAL company record (P-B): identity + logo, the same for every user. */
  company?: Record<string, unknown>
}

/**
 * One folded SearchRun as `GET /api/search/runs` returns it (the durable-resume read).
 * `id` IS the searchId — the domain names it `id` on the aggregate (`SearchRun.id: Urn`,
 * `search-run.domain.ts:34`) while the start/stop procedures call the same value `searchId`.
 */
interface RecentSearchRun {
  id: string
  startedAt: string
}

/** What `POST /api/applications/generate` answers with (S11 — same shape the applications surface reads). */
interface GenerateResponse {
  applicationId: string
  generationId: string
  streamId: string
  applicationStreamId: string
}

/** Which slice of the pipeline the list is showing. */
type PipelineFilter = "alle" | "gemerkt" | "dringend"

type SearchConfigListKey = "roles" | "queries" | "seniority" | "locations" | "excludeKeywords"
const CONFIG_FIELDS: { key: SearchConfigListKey; label: CareerKey }[] = [
  { key: "roles", label: "workshop.targeting.roles" },
  { key: "queries", label: "workshop.targeting.queries" },
  { key: "seniority", label: "workshop.targeting.seniority" },
  { key: "locations", label: "workshop.targeting.locations" },
  { key: "excludeKeywords", label: "workshop.targeting.exclusions" },
]

// The live-rail vocabulary is the generic search-run wording (shared with heartbeat).
const STAGE_KEY: Record<SearchRunStage, CareerKey> = {
  discovering: "heartbeat.stage.discovering",
  verifying: "heartbeat.stage.verifying",
  matching: "heartbeat.stage.matching",
  enriching: "heartbeat.stage.enriching",
  done: "heartbeat.stage.done",
}
const STATUS_KEY: Record<SearchRunStatus, CareerKey> = {
  running: "heartbeat.status.running",
  completed: "heartbeat.status.completed",
  stopped: "heartbeat.status.stopped",
  failed: "heartbeat.status.failed",
}

const POLL_MS = 2500

/**
 * The SearchRun rail is fed ONLY by the push plane, opened during `start()`. On a cold load —
 * a hard reload, a new tab, or a re-login after the token expired MID-run — that subscription is
 * gone, so a run that FAILED (or completed) while the surface was away simply vanished from view:
 * the operator watched »entdeckt 28« then »fehlgeschlagen«, navigated, came back, and saw an empty
 * rail. This used to be patched with a `sessionStorage` note of the one stream this browser had
 * opened, which dies with the tab and knows nothing about a run started on another machine.
 *
 * The durable home is the server: `GET /api/search/runs` reads the caller's own SearchRun streams
 * (they were always there — this is a read path, not a new event). Re-subscribing to the newest
 * one lets the push plane REPLAY the stream and fold the run — terminal state, counts and error —
 * right back into the rail, on any device, for as long as the run exists.
 */
const searchRunStream = (userId: string, searchId: string): string => careerStream(userId, "SearchRun", searchId)

export class SearchVm implements ViewModel {
  readonly runs: ReadonlySignal<readonly SearchRunView[]>
  readonly opportunities: ReadonlySignal<readonly OpportunityCardView[]>
  readonly bookmarked: ReadonlySignal<readonly OpportunityCardView[]>
  /** The deadline-pressured slice (S10b) — surfaced above the pipeline AND filterable. */
  readonly urgent: ReadonlySignal<readonly OpportunityCardView[]>
  /** Which slice the ONE list is showing (S10c) — there is no second list to fall out of sync. */
  readonly filter: Signal<PipelineFilter> = signal<PipelineFilter>("alle")
  readonly config: Signal<SearchConfig | null> = signal<SearchConfig | null>(null)
  readonly targetingReady: Signal<boolean> = signal(true)
  /** The hub-header readiness signal: is there a confirmed Werte-Sektion to search FROM? (D2 §3). */
  readonly werteReady: Signal<boolean> = signal(false)
  readonly activeSearchId: Signal<string | null> = signal<string | null>(null)
  readonly starting: Signal<boolean> = signal(false)
  /** An application generation kicked off from a result card (S11) is in flight. */
  readonly generating: Signal<boolean> = signal(false)
  readonly stopping: Signal<boolean> = signal(false)
  readonly error: Signal<string | null> = signal<string | null>(null)
  /** The search was refused because these exact parameters just came back with nothing — the
   *  identical-re-run gate (`searchInputGate`). Change the targeting to search again. */
  readonly inputBlocked: Signal<boolean> = signal(false)
  readonly loaded: Signal<boolean> = signal(false)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]
  /** SearchRun streams opened this session (subscribe REPLACES the set). */
  private readonly streams = new Set<string>()
  /** Monotonic version stamp for fetch-reconciled synthetic envelopes (idempotency law). */
  private seq = 1
  private timer: ReturnType<typeof setInterval> | null = null

  /** This surface's claim on the shared push channel (`lib/push-streams.ts`): the channel
   *  subscription is the UNION of every mounted surface's claim, never just ours. */
  private readonly claim: StreamClaim

  constructor(
    private readonly searchStore: SearchRunStore,
    private readonly store: OpportunityStore,
    push: PushChannel,
    private readonly router: Router,
    private readonly session: ShellSession,
    i18n: CareerI18n,
  ) {
    this.t = i18n.t
    this.claim = createStreamClaim(push)
    this.runs = searchStore.list("aktiv")
    this.opportunities = store.list("alle")
    this.bookmarked = store.list("gemerkt")
    this.urgent = store.list("dringend")
    this.renderDeps = [
      this.runs,
      this.opportunities,
      this.bookmarked,
      this.urgent,
      this.filter,
      this.config,
      this.targetingReady,
      this.werteReady,
      this.activeSearchId,
      this.starting,
      this.generating,
      this.stopping,
      this.error,
      this.inputBlocked,
      this.loaded,
      store.pending,
    ]
  }

  private get userId(): string | null {
    return this.session.user?.sub ?? null
  }

  attach(): () => void {
    void this.hydrate()
    void this.loadReadiness()
    void this.resumeLastRun()
    return () => {
      this.stopPolling()
      this.claim.release()
    }
  }

  /**
   * Re-open the caller's most recent search run from the SERVER (see `searchRunStream`). The push
   * plane replays the stream on subscribe, so a run that FAILED or completed while the surface was
   * gone folds back into the rail with its counts + error — the »entdeckt 28 · fehlgeschlagen«
   * state the operator last saw, never a blank page — and it now survives a closed tab and a
   * different machine, which the old sessionStorage note could not. Polling then reconciles any
   * opportunities it did persist and self-stops the moment the run reports it is no longer running.
   * A failure here is silent by design: resuming a view of the past must never break the present.
   */
  private async resumeLastRun(): Promise<void> {
    const userId = this.userId
    if (!userId) return
    const recent = await apiGet<RecentSearchRun[]>("/api/search/runs").catch(() => null)
    const last = recent?.[0]
    if (!last?.id) return
    const streamId = searchRunStream(userId, last.id)
    if (this.streams.has(streamId)) return
    this.streams.add(streamId)
    this.activeSearchId.set(last.id)
    this.claim.claim([...this.streams])
    this.startPolling()
  }

  /** Fold the hub-header readiness — the search brief rests on a confirmed Werte-Sektion. Defensive:
   *  a missing/odd payload leaves readiness false (the honest »schärfe zuerst dein Profil« state). */
  private async loadReadiness(): Promise<void> {
    const sektion = await apiGet<{ produced?: boolean }>("/api/workshops/values/sektion").catch(() => null)
    this.werteReady.set(Boolean(sektion?.produced))
  }

  /** The run currently being driven (the started search), if any. */
  currentRun(): SearchRunView | null {
    const id = this.activeSearchId.value
    return id ? (this.searchStore.entity(id as Urn).value ?? null) : (this.runs.value[0] ?? null)
  }

  isRunning(): boolean {
    return this.currentRun()?.status === "running"
  }

  /**
   * Set the surface error from a caught exception — EXCEPT the credits-block (402 insufficient /
   * entitlement 403): that's not a surface failure, it's a product state the shared shell notice
   * already owns (link to /credits). Swallow it here so the surface stays calm (its empty state)
   * instead of shouting a raw „…failed: HTTP 403". Real failures still surface normally.
   */
  private setError(e: unknown): void {
    if (isPaymentRequired(e)) {
      this.error.set(null)
      return
    }
    this.error.set(e instanceof Error ? e.message : String(e))
  }

  async start(): Promise<void> {
    this.starting.set(true)
    this.error.set(null)
    this.inputBlocked.set(false)
    try {
      const res = await apiPost<StartResponse>("/api/search/start")
      this.config.set(res.config)
      this.targetingReady.set(res.targetingReady)
      this.activeSearchId.set(res.searchId)
      this.streams.add(res.streamId)
      // Nothing to remember client-side: the run IS the durable record, and `resumeLastRun`
      // reads it back from the server on the next cold load.
      // Live rail: the SearchRun aggregate carries liveness over the push plane.
      this.claim.claim([...this.streams])
      await this.hydrate() // reconcile any results already written, then poll for more
      this.startPolling()
    } catch (e) {
      // The identical-re-run gate is a product state, not a failure: these exact parameters just
      // delivered nothing, so re-running them would only hit the providers again for the same
      // empty result. Reflect it as a calm "change your parameters" hint, not a raw error banner.
      if (isSearchInputUnchanged(e)) this.inputBlocked.set(true)
      else this.setError(e)
    } finally {
      this.starting.set(false)
    }
  }

  async stop(): Promise<void> {
    const id = this.activeSearchId.value
    if (!id) return
    this.stopping.set(true)
    this.error.set(null)
    try {
      await apiPost("/api/search/stop", { searchId: id })
    } catch (e) {
      this.setError(e)
    } finally {
      this.stopping.set(false)
      await this.hydrate() // final reconcile
      this.stopPolling()
    }
  }

  /** The slice the ONE list shows. Purely a view choice — no fetch, no server round-trip. */
  setFilter(next: PipelineFilter): void {
    this.filter.set(next)
  }

  /** The results behind the active filter, already ordered by evaluation (the store projects it). */
  visible(): readonly OpportunityCardView[] {
    const by: Record<PipelineFilter, readonly OpportunityCardView[]> = {
      alle: this.opportunities.value,
      gemerkt: this.bookmarked.value,
      dringend: this.urgent.value,
    }
    return by[this.filter.value]
  }

  bookmark(opportunityId: string): void {
    void this.store.command({ type: "bookmark", payload: { opportunityId } })
  }
  dismiss(opportunityId: string): void {
    void this.store.command({ type: "dismiss", payload: { opportunityId } })
  }
  remove(opportunityId: string): void {
    void this.store.command({ type: "delete", payload: { opportunityId } })
  }

  /**
   * Start an application for this Stelle (S11) — the same detached generation the applications
   * surface starts. Both stream ids are REMEMBERED before we navigate, so the review surface's
   * cold-load resume plays the rail from the first event rather than from wherever we arrive.
   * The card's `data-opportunity` is the FULL urn (what bookmark/dismiss use); the api wants
   * the short id, hence the conversion here rather than a second id shape in the markup.
   */
  async generateApplication(opportunityUrn: string): Promise<void> {
    if (this.generating.value) return
    this.generating.set(true)
    try {
      const res = await apiPost<GenerateResponse>("/api/applications/generate", { opportunityId: opportunityShortId(opportunityUrn) })
      writeLastGeneration(this.userId, { streamId: res.streamId, applicationStreamId: res.applicationStreamId })
      this.claim.claim([...this.streams, res.streamId, res.applicationStreamId])
      void this.router.navigate(`/applications/${res.applicationId.replace(/^urn:career:application:/, "")}`)
    } catch (e) {
      this.setError(e)
    } finally {
      this.generating.set(false)
    }
  }

  go(url: string): void {
    void this.router.navigate(url)
  }

  private startPolling(): void {
    this.stopPolling()
    this.timer = setInterval(() => {
      void this.hydrate().then(() => {
        if (!this.isRunning()) this.stopPolling()
      })
    }, POLL_MS)
  }
  private stopPolling(): void {
    if (this.timer !== null) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  /** Reconcile the owner's opportunities into the store (each at a fresh version). */
  private async hydrate(): Promise<void> {
    const userId = this.userId
    try {
      const items = await apiGet<OpportunityWithMatch[]>("/api/opportunities")
      if (userId) {
        for (const { opportunity, match, org, company } of items) {
          const id = opportunity["id"]
          if (typeof id !== "string") continue
          const streamId = careerStream(userId, "Opportunity", id)
          this.store.apply({
            streamId,
            version: this.seq++,
            type: "opportunity.Discovered",
            payload: { owner: userId, opportunity, ...(org ? { org } : {}), ...(company ? { company } : {}) },
          })
          if (match) {
            this.store.apply({ streamId, version: this.seq++, type: "opportunity.Matched", payload: { owner: userId, opportunityId: id, match } })
          }
        }
      }
    } catch (e) {
      this.setError(e)
    } finally {
      this.loaded.set(true)
    }
  }
}

export class CareerSearch extends KitElement<SearchVm> {
  protected createViewModel(ctx: KitContext): SearchVm {
    return new SearchVm(
      ctx.inject(SearchRunStoreToken),
      ctx.inject(OpportunityStoreToken),
      ctx.inject(PushToken),
      ctx.inject(RouterToken),
      ctx.inject(SessionToken),
      ctx.inject(I18nToken),
    )
  }

  /** The effective search targeting — a seamless labelled reading (§0), never a boxed panel. */
  private configPanel(vm: SearchVm): TemplateResult {
    const cfg = vm.config.value
    if (!cfg) return nothing as unknown as TemplateResult
    return html`
      <section class="search-config" aria-labelledby="search-config-title">
        <header class="ws-sec-head">
          <h2 id="search-config-title" class="ws-sec-label" lang="de">${vm.t("search.config.title")}</h2>
        </header>
        ${cfg.summary ? html`<p class="search-config-summary" lang="de">${cfg.summary}</p>` : nothing}
        <dl class="search-config-list">
          ${CONFIG_FIELDS.map(
            (f) => html`<dt lang="de">${vm.t(f.label)}</dt>
              <dd lang="de">${cfg[f.key].length > 0 ? cfg[f.key].join(", ") : vm.t("search.config.empty")}</dd>`,
          )}
        </dl>
      </section>
    `
  }

  /** One live-rail row — a flat hairline row: lifecycle mark · status + stage · the three
   *  counts-with-meaning as one tabular reading. A FAILED run must never read as a merely
   *  quiet one: the fold already carries `run.error` (`searchRun.Failed`), so the row says so
   *  in the shared degraded note — status-gated, not error-gated, so a failure without a
   *  reason still surfaces honestly. */
  private railRun(vm: SearchVm, run: SearchRunView): TemplateResult {
    return html`
      <li class=${classMap({ "search-run": true, [`is-${run.status}`]: true })} data-run-id=${run.urn}>
        <span class="srun-mark" aria-hidden="true"></span>
        <div class="srun-body">
          <p class="srun-line" lang="de">
            <span class="run-status" lang="de">${vm.t(STATUS_KEY[run.status])}</span>
            ${run.status === "running" ? html`<span class="srun-stage" lang="de"> · ${vm.t(STAGE_KEY[run.stage])}</span>` : nothing}
          </p>
          <p class="srun-figures" lang="de">
            <span class="run-count" lang="de">${vm.t("heartbeat.count.discovered", { n: run.discovered })}</span>
            <span class="srun-sep" aria-hidden="true">·</span>
            <span class="run-count" lang="de">${vm.t("heartbeat.count.verified", { n: run.verified })}</span>
            <span class="srun-sep" aria-hidden="true">·</span>
            <span class="run-count" lang="de">${vm.t("heartbeat.count.matched", { n: run.matched })}</span>
          </p>
          ${run.status === "failed"
            ? degradedNote({
                label: vm.t("search.run.failed.label"),
                message: vm.t("search.run.failed.message"),
                detail: run.error ? vm.t("search.run.failed.reason", { reason: run.error }) : undefined,
              })
            : nothing}
        </div>
      </li>
    `
  }

  /** The hub header — search state (results so far) + the honest readiness of the brief you search
   *  FROM. The ONE primary CTA is state-derived: ready → start the run; not ready → sharpen first. */
  private hero(vm: SearchVm): TemplateResult {
    const opps = vm.opportunities.value
    const ready = vm.werteReady.value
    const meta = opps.length > 0 ? vm.t("search.hub.meta.results", { n: opps.length }) : vm.t("search.hub.meta.none")
    return hubHero({
      eyebrow: vm.t("search.hub.eyebrow"),
      title: ready ? vm.t("search.hub.title.ready") : vm.t("search.hub.title.notReady"),
      lead: ready ? vm.t("search.hub.lead.ready") : vm.t("search.hub.lead.notReady"),
      meta,
      cta: vm.isRunning()
        ? undefined
        : vm.inputBlocked.value
          ? // These exact parameters just delivered nothing — the primary action is to change them.
            { label: vm.t("search.unchanged.action"), act: "nav", url: "/workshops/search-profile" }
          : ready
            ? { label: vm.t("search.start"), act: "start-search", pending: vm.starting.value }
            : { label: vm.t("search.hub.cta.sharpen"), act: "nav", url: "/workshops/values/sektion" },
    })
  }

  protected render(vm: SearchVm): TemplateResult {
    const runs = vm.runs.value
    const opps = vm.opportunities.value
    const saved = vm.bookmarked.value
    const running = vm.isRunning()
    const err = vm.error.value
    return html`
      <section class="surface search search-hub" aria-labelledby=${HUB_TITLE_ID}>
        ${this.hero(vm)}
        ${err ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: err })}</p>` : nothing}

        ${vm.inputBlocked.value
          ? html`<p class="search-unchanged-hint surface-gated" role="status" lang="de">
              ${vm.t("search.unchanged.message")}
              <a href="/workshops/search-profile" data-act="nav" data-url="/workshops/search-profile" lang="de">${vm.t("search.unchanged.action")}</a>
            </p>`
          : nothing}

        ${running
          ? html`<div class="search-controls" role="group" aria-label=${vm.t("surface.suche.title")}>
              <button
                data-act="stop-search"
                class=${classMap({ "search-stop": true, "is-pending": vm.stopping.value })}
                ?disabled=${vm.stopping.value}
                lang="de"
              >
                ${vm.stopping.value ? vm.t("search.stopping") : vm.t("search.stop")}
              </button>
            </div>`
          : nothing}

        ${!vm.targetingReady.value
          ? html`<p class="search-targeting-hint surface-gated" lang="de">
              ${vm.t("search.targetingHint")}
              <a href="/workshops/search-profile" data-act="nav" data-url="/workshops/search-profile" lang="de">${vm.t("search.targetingHint.action")}</a>
            </p>`
          : nothing}

        ${this.configPanel(vm)}

        <section class="search-rail-section" aria-labelledby="search-rail-title">
          <header class="ws-sec-head">
            <h2 id="search-rail-title" class="ws-sec-label" lang="de">${vm.t("search.rail.title")}</h2>
          </header>
          <ol class="search-rail" role="log" aria-live="polite" aria-label=${vm.t("search.rail.title")}>
            ${runs.length === 0
              ? html`<li class="search-rail-empty" lang="de">${vm.t("search.rail.empty")}</li>`
              : repeat(runs, (r) => r.urn, (r) => this.railRun(vm, r))}
          </ol>
        </section>

        ${this.urgentBand(vm)}
        ${this.pipeline(vm)}
        ${this.goldenNext(vm)}
      </section>
    `
  }

  /**
   * The urgent band (S10b) — postings whose stated deadline is inside a week, surfaced ABOVE the
   * pipeline. Deliberately a COMPACT pointer list, not a second set of cards: the whole point of
   * the single filtered list below is that no result renders twice on one page. It also stays out
   * of the ordering — a near deadline must never let a weak match outrank a strong one — and it
   * hides itself when the list is already filtered to exactly this slice.
   */
  private urgentBand(vm: SearchVm): TemplateResult | typeof nothing {
    const urgent = vm.urgent.value
    if (urgent.length === 0 || vm.filter.value === "dringend") return nothing
    return html`
      <section class="search-urgent" aria-labelledby="search-urgent-title">
        <header class="ws-sec-head">
          <h2 id="search-urgent-title" class="ws-sec-label" lang="de">${vm.t("search.urgent.title")}</h2>
          <span class="ws-sec-count">${urgent.length}</span>
        </header>
        <p class="search-urgent-lead" lang="de">${vm.t("search.urgent.lead")}</p>
        <ul class="urgent-list" role="list">
          ${repeat(
            urgent,
            (o) => o.urn,
            (o) => {
              const days = urgencyOf(o)?.days ?? 0
              return html`<li class="urgent-row">
                <span class="urgent-days" lang="de">${vm.t("opportunity.flag.deadline", { n: days })}</span>
                <a
                  class="urgent-title"
                  href=${`/search/opportunities/${opportunityShortId(o.id)}`}
                  data-act="open-opportunity"
                  data-opportunity=${o.urn}
                  lang="de"
                  >${o.title}</a
                >
                <span class="urgent-company" lang="de">${o.company?.name ?? o.employer}</span>
              </li>`
            },
          )}
        </ul>
      </section>
    `
  }

  /** One filter chip — a view choice with its own honest count (S1: a count is fine when singular). */
  private filterChip(vm: SearchVm, value: PipelineFilter, label: CareerKey, n: number): TemplateResult {
    return html`<button
      class="pipe-filter-btn"
      data-act="set-pipeline-filter"
      data-filter=${value}
      aria-pressed=${vm.filter.value === value ? "true" : "false"}
      lang="de"
    >
      ${vm.t(label)}<span class="pipe-filter-count">${n}</span>
    </button>`
  }

  /**
   * THE pipeline (S10c) — one durable, evaluation-ordered list, grouped by company, filtered in
   * place. It replaces the old »Passende Stellen« + »Gemerkt« pair, which rendered every saved
   * result twice on the same page; a filter cannot disagree with itself the way two lists could.
   */
  private pipeline(vm: SearchVm): TemplateResult {
    const running = vm.isRunning()
    const visible = vm.visible()
    const groups = groupByCompany(visible)
    const filter = vm.filter.value
    const emptyCopy: Record<PipelineFilter, string> = {
      alle: running ? vm.t("search.results.running") : vm.t("search.results.empty"),
      gemerkt: vm.t("search.filter.empty.saved"),
      dringend: vm.t("search.filter.empty.urgent"),
    }
    return html`
      <section class="search-results" aria-labelledby="search-results-title" aria-live="polite">
        <header class="ws-sec-head">
          <h2 id="search-results-title" class="ws-sec-label" lang="de">${vm.t("search.pipeline.title")}</h2>
          ${visible.length > 0 ? html`<span class="ws-sec-count">${visible.length}</span>` : nothing}
        </header>
        <p class="search-pipeline-lead" lang="de">${vm.t("search.pipeline.lead")}</p>

        <!-- No pipeline, no filter: three chips reading 0 would be chrome for a page that has
             nothing to filter, and the first-visit empty state should say one thing only. -->
        ${vm.opportunities.value.length > 0
          ? html`<div class="pipe-filter" role="group" aria-label=${vm.t("search.filter.label")}>
              <span class="pipe-filter-label" aria-hidden="true" lang="de">${vm.t("search.filter.label")}</span>
              ${this.filterChip(vm, "alle", "search.filter.all", vm.opportunities.value.length)}
              ${this.filterChip(vm, "gemerkt", "search.filter.saved", vm.bookmarked.value.length)}
              ${vm.urgent.value.length > 0 ? this.filterChip(vm, "dringend", "search.filter.urgent", vm.urgent.value.length) : nothing}
            </div>`
          : nothing}

        ${groups.length === 0
          ? !vm.loaded.value && !running && filter === "alle"
            ? html`<ul class="opportunity-list" role="list" aria-busy="true">${skeletonOpportunityRows(3)}</ul>`
            : html`<p class="surface-empty" lang="de">${emptyCopy[filter]}</p>`
          : html`<ul class="opportunity-list" role="list">
              ${repeat(groups, (g) => g.key, (g) => renderOpportunityGroup(g, vm.t))}
            </ul>`}

        <!-- Refill, at the END of the list, which is where a worked-off pipeline actually is.
             It composes the R2 action family rather than re-deriving a button (WS-A §6). -->
        ${visible.length > 0 && !running
          ? html`<div class="pipe-refill">
              ${action({
                act: "start-search",
                label: vm.starting.value ? vm.t("search.starting") : vm.t("search.refill"),
                variant: "quiet",
                intent: "refresh",
                pending: vm.starting.value,
              })}
              <p class="pipe-refill-hint" lang="de">${vm.t("search.refill.hint")}</p>
            </div>`
          : nothing}
      </section>
    `
  }

  /** The golden-path spine forward (D4), subordinate to the hero's search CTA: onward to the
   *  applications pipeline (always available — your applications live there once you generate one). */
  private goldenNext(vm: SearchVm): TemplateResult | typeof nothing {
    const decl = spineDecl("career-search")
    if (!decl) return nothing
    return nextStep(vm.t("next.eyebrow"), toNextStep(decl, vm.t(decl.labelKey)))
  }

  protected override actions(vm: SearchVm): ActionMap {
    return {
      "start-search": () => void vm.start(),
      "stop-search": () => void vm.stop(),
      "generate-application": ({ opportunity }) => {
        if (opportunity) void vm.generateApplication(opportunity)
      },
      "set-pipeline-filter": ({ filter }) => {
        if (filter === "alle" || filter === "gemerkt" || filter === "dringend") vm.setFilter(filter)
      },
      bookmark: ({ opportunity }) => {
        if (opportunity) vm.bookmark(opportunity)
      },
      dismiss: ({ opportunity }) => {
        if (opportunity) vm.dismiss(opportunity)
      },
      delete: ({ opportunity }) => {
        if (opportunity) vm.remove(opportunity)
      },
      "open-opportunity": ({ opportunity }, event) => {
        event.preventDefault()
        if (opportunity) vm.go(`/search/opportunities/${opportunityShortId(opportunity)}`)
      },
      nav: ({ url }, event) => {
        event.preventDefault()
        if (url) vm.go(url)
      },
    }
  }
}
